import { useCallback, useRef } from 'react';
import { simulatableCreatePerpMarketOrderIxs } from '../trade/perp/useOpenPerpMarketOrder';
import useCurrentUserAccount from '../useCurrentUserAccount';
import useVelocityClient from '../useVelocityClient';
import { useUserProtectionSlotClock } from 'src/hooks/useSlotClock';
import {
	VelocityClient,
	PositionDirection,
	BigNum,
	BASE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { ORDER_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/orderPrep';
import {
	syncGetCurrentSettings,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import { PerpTradeFormOutputProps } from 'src/stores/VelocityStore/actions/actionHelpers/types';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import { useGetTxParams } from '../velocityActions/transactions/useGetTxParams';
import {
	createCancelOrdersIx,
	createSettlePnlIx,
	MarketId,
	OpenPosition,
	UIMarket,
} from '@velocity-exchange/common';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getUserMaxLeverageForMarket } from 'src/utils/position';
import { generateUserOrderIdForUser } from 'src/utils/order';
import {
	PublicKey,
	Transaction,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import {
	AnchorError,
	isTransactionTooLargeError,
} from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { useTxErrorHandler } from '../velocityActions/transactions/useTxErrorHandler';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignVelocityActionWithFallback } from 'src/hooks/wallets/useAutoSignVelocityActionWithFallback';
import invariant from 'tiny-invariant';
import { runOrderActionToast } from '../velocityActions/transactions/runOrderActionToast';
import { useCurrentAccountPositions } from '../query/accountTradingData/usePositions';
import { useCurrentAccountOrders } from '../query/accountTradingData/useOrders';
import {
	batchInstructionsBySize,
	isTransactionSizeError,
} from 'src/utils/transaction';
import usePostHogCapture from '../posthog/usePostHogCapture';
import { FEE_PAYER_WALLET } from 'src/utils/wallets/feePayerWallet';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import {
	OrderToastProps,
	TRADE_PREP_UTILS,
} from 'src/stores/VelocityStore/actions/actionHelpers/tradePrep';

interface ClosePositionInstructionSet {
	marketIndex: number;
	instructions: TransactionInstruction[];
}

/**
 * Creates a build transaction function that uses the fee payer wallet.
 * This is used for size estimation during batching.
 */
function createBuildTransactionForSizing(
	velocityClient: VelocityClient,
	feePayerPublicKey: PublicKey
) {
	return async (ixs: TransactionInstruction[]) => {
		const initialWallet = velocityClient.txHandler.getWallet();
		try {
			velocityClient.txHandler.updateWallet({
				publicKey: feePayerPublicKey,
				signTransaction: async (tx) => tx,
				signAllTransactions: async (txs) => txs,
			});
			const tx = await velocityClient.buildTransaction(ixs, {
				useSimulatedComputeUnits: false, // Skip simulation for size checking
			});
			return tx;
		} finally {
			velocityClient.txHandler.updateWallet(initialWallet);
		}
	};
}

/**
 * Hook to market close multiple positions at once.
 *
 * Settle pnl for the given markets, once the positions are closed. (handled in `useAutomatedTxnsListener`)
 *
 * Has the ability to create cancel orders for the given markets, once the positions are closed too. (handled in `useAutomatedTxnsListener`)
 *
 * Transactions are batched by size to avoid exceeding Solana's transaction size limits.
 * Batches are sent in parallel for faster execution.
 */
export default function useCloseMultiplePositions() {
	const currentUserAccount = useCurrentUserAccount();
	const velocityClient = useVelocityClient();
	const appEventEmitter = useAppEventEmitter();
	const { captureEvent } = usePostHogCapture();

	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();
	const { slotDurationMs } = useUserProtectionSlotClock();
	const setVelocityStore = useVelocityStore((s) => s.set);

	const openPerpPositions = useCurrentAccountPositions();
	const openOrders = useCurrentAccountOrders();
	const openPerpPositionsRef = useRef(openPerpPositions);
	openPerpPositionsRef.current = openPerpPositions;
	const openOrdersRef = useRef(openOrders);
	openOrdersRef.current = openOrders;

	const closeMultiplePositions = useAutoSignVelocityActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				marketIndexes: number[],
				cancelExistingOrders?: boolean
			) => {
				const userClient = currentUserAccount?.client;
				invariant(userClient, 'userClient is required');
				invariant(velocityClient, 'driftClient is required');
				invariant(
					openPerpPositionsRef.current.length > 0,
					'openPerpPositions is required'
				);

				const result = await runOrderActionToast({
					toastId,
					isAutoSigned,
					pendingMessage: 'Closing positions...',
					run: async () => {
						const settings = syncGetCurrentSettings();
						const isPlaceAndTake =
							velocityClient.txVersion === 0 && !!settings?.placeAndTakeEnabled;

						// Get the initial user order ID before the loop
						const initialUserOrderId = generateUserOrderIdForUser(userClient);

						// Build close position instructions for each market separately
						const closePositionIxSets: ClosePositionInstructionSet[] = [];

						const positions = marketIndexes
							.map((marketIndex) => {
								const position = openPerpPositionsRef.current.find(
									(pos) => pos.marketIndex === marketIndex
								);
								if (!position) {
									console.error('Position not found for market', marketIndex);
									return undefined;
								}
								return position;
							})
							.filter(
								(position): position is OpenPosition => position !== undefined
							);

						// Per-order market order toasts, keyed by market index. Entries are
						// removed as each order reaches a terminal state, so whatever is
						// left is still waiting on a toast update.
						const pendingToastProps = new Map<number, OrderToastProps>();

						const abortToasts = (marketIndexesToAbort: number[]) => {
							marketIndexesToAbort.forEach((marketIndex) => {
								const toastProps = pendingToastProps.get(marketIndex);
								if (!toastProps) return;
								pendingToastProps.delete(marketIndex);
								appEventEmitter.emit('expectedAuctionTxAborted', toastProps);
							});
						};

						try {
							// Build instructions for each position
							await Promise.all(
								positions.map(async (position, index) => {
									const marketIndex = position.marketIndex;

									const uiMarket = UIMarket.createPerpMarket(marketIndex);
									const { optionalAuctionParamsInputs } =
										ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps(
											{
												leadSide: 'base',
												targetMarketIndex: marketIndex,
												currentSettings: settings,
												perpMarketAccount:
													velocityClient.getPerpMarketAccountOrThrow(
														marketIndex
													),
												side: position.direction === 'long' ? 'sell' : 'buy',
												baseSizeStringValue: position.baseSize.abs().toString(),
												reduceOnly: true,
												orderType: 'market',
												slippageTolerance:
													settings?.slippageTolerance ?? 'dynamic',
											} as PerpTradeFormOutputProps & {
												leadSide: 'base' | 'quote';
											},
											slotDurationMs
										);

									const txParams = getTxParams({
										computeUnits: isPlaceAndTake
											? MAX_COMPUTE_UNITS
											: undefined,
										marketId: MarketId.createPerpMarket(marketIndex),
										simulatedComputeUnitMultiplier: isPlaceAndTake
											? 1.4
											: undefined,
										lowerBoundCu: isPlaceAndTake
											? Env.placeAndTakeLowerBoundCu
											: undefined,
									});

									const positionMaxLeverage = getUserMaxLeverageForMarket(
										marketIndex,
										velocityClient,
										userClient,
										uiMarket
									);

									const userOrderId = initialUserOrderId + index;
									const direction =
										position.direction === 'long'
											? PositionDirection.SHORT
											: PositionDirection.LONG;

									// Same per-order toast as a single "Market Close", hidden until
									// it fills so the batch toast alone covers the wait.
									const toastProps = TRADE_PREP_UTILS.getOrderToastProps({
										marketId: MarketId.createPerpMarket(marketIndex),
										direction,
										baseSize: BigNum.from(
											position.baseSize.abs(),
											BASE_PRECISION_EXP
										),
										numOrders: 1,
										currentSettings: settings as Partial<UserSettings>,
										orderType: 'market',
										userOrderId,
									});
									pendingToastProps.set(marketIndex, toastProps);
									appEventEmitter.emit('expectedAuctionInit', {
										...toastProps,
										isAutoSigned,
										deferUntilFill: true,
									});

									const closePositionIxs =
										await simulatableCreatePerpMarketOrderIxs({
											velocityClient,
											mainSignerOverride: signerWallet.publicKey,
											user: userClient,
											slotDuration: slotDurationMs,
											marketIndex,
											direction,
											baseAmount: position.baseSize.abs(),
											optionalAuctionParamsInputs,
											reduceOnly: true,
											placeAndTake: {
												enable: isPlaceAndTake,
												auctionDurationPercentage:
													Env.defaultAuctionDurationPercentage,
											},
											txParams,
											positionMaxLeverage,
											userOrderId,
										});

									closePositionIxSets.push({
										marketIndex,
										instructions: Array.isArray(closePositionIxs)
											? closePositionIxs
											: [closePositionIxs],
									});
								})
							);

							if (closePositionIxSets.length === 0) {
								throw new Error('No close position instructions found');
							}

							// Build cancel orders and settle pnl instructions (kept as separate txs)
							let cancelOrdersIx: TransactionInstruction | undefined;
							if (cancelExistingOrders) {
								const existingOrderIdsToCancel = openOrdersRef.current
									.filter((order) => marketIndexes.includes(order.marketIndex))
									.map((order) => order.orderId);
								cancelOrdersIx = await createCancelOrdersIx({
									velocityClient: velocityClient,
									user: userClient,
									orderIds: existingOrderIdsToCancel,
									mainSignerOverride: signerWallet.publicKey,
								});
							}

							const settlePnlIx = await createSettlePnlIx({
								velocityClient: velocityClient,
								user: userClient,
								marketIndexes,
								mainSignerOverride: signerWallet.publicKey,
							});

							// Batch close position instructions by transaction size
							const buildTransactionForSizing = createBuildTransactionForSizing(
								velocityClient,
								FEE_PAYER_WALLET.publicKey
							);

							const { batches, batchSizes } = await batchInstructionsBySize(
								closePositionIxSets,
								buildTransactionForSizing
							);

							// Log batching telemetry
							captureEvent('trade_close_multiple_positions_batched', {
								total_positions: closePositionIxSets.length,
								batch_count: batches.length,
								batch_sizes: batchSizes,
								is_place_and_take: isPlaceAndTake,
								market_indexes: marketIndexes,
							});

							const txParams = getTxParams({
								computeUnits: isPlaceAndTake ? MAX_COMPUTE_UNITS : undefined,
								simulatedComputeUnitMultiplier: isPlaceAndTake
									? 1.4
									: undefined,
								lowerBoundCu: isPlaceAndTake
									? Env.placeAndTakeLowerBoundCu
									: undefined,
							});

							// Build and sign all batches plus settle/cancel txs
							const ixsToSign: Record<string, TransactionInstruction[]> = {};

							// Add close position batches
							batches.forEach((batch, index) => {
								const batchIxs = batch.flatMap((set) => set.instructions);
								ixsToSign[`closePositions_${index}`] = batchIxs;
							});

							// Add settle and cancel as separate txs
							if (settlePnlIx) {
								ixsToSign['settlePnl'] = [settlePnlIx];
							}
							if (cancelOrdersIx) {
								ixsToSign['cancelOrders'] = [cancelOrdersIx];
							}

							const { signedTxData, signedTxMap } =
								await signerWallet.buildAndSignTransactionsMap(
									ixsToSign,
									txParams
								);

							const batchEntries = batches.map((batch, index) => ({
								marketIndexes: batch.map((set) => set.marketIndex),
								signedTx: signedTxMap[`closePositions_${index}`],
							}));

							const successfulTxSigs: string[] = [];
							const failedBatches: string[] = [];
							const failedMarketNames: string[] = [];

							// Failures are aggregated into the batch toast below, so drop the
							// per-order toasts rather than showing one red toast per position.
							const failBatch = (
								batchMarketIndexes: number[],
								reason: string
							) => {
								failedBatches.push(reason);
								failedMarketNames.push(
									...batchMarketIndexes.map(
										(marketIndex) =>
											UIMarket.createPerpMarket(marketIndex).market.symbol
									)
								);
								abortToasts(batchMarketIndexes);
							};

							// The wallet layer swallows signing failures into a missing entry
							// instead of throwing, so an unsigned batch counts as failed.
							const signedBatches = batchEntries.filter((entry) => {
								if (entry.signedTx) return true;
								failBatch(entry.marketIndexes, 'signing_failed');
								return false;
							});

							signedBatches.forEach(
								({ marketIndexes: batchMarketIndexes, signedTx }) => {
									const batchSignedTxData = signedTxData.filter(
										(data) => data.signedTx === signedTx
									);
									batchMarketIndexes.forEach((marketIndex) => {
										const toastProps = pendingToastProps.get(marketIndex);
										if (!toastProps) return;
										appEventEmitter.emit('expectedAuctionTxSigned', {
											...toastProps,
											signedTxData: batchSignedTxData,
										});
									});
								}
							);

							// Send all signed close position batches in parallel
							const sendResults = await Promise.allSettled(
								signedBatches.map(async ({ signedTx }) => {
									const { txSig } = await velocityClient.sendTransaction(
										signedTx as VersionedTransaction,
										undefined,
										undefined,
										true
									);
									return txSig;
								})
							);

							sendResults.forEach((settledResult, index) => {
								const { marketIndexes: batchMarketIndexes } =
									signedBatches[index];

								if (
									settledResult.status === 'fulfilled' &&
									settledResult.value
								) {
									successfulTxSigs.push(settledResult.value);
									batchMarketIndexes.forEach((marketIndex) => {
										const toastProps = pendingToastProps.get(marketIndex);
										if (!toastProps) return;
										pendingToastProps.delete(marketIndex);
										appEventEmitter.emit(
											'expectedAuctionTxConfirmed',
											toastProps
										);
									});
									return;
								}

								const error =
									settledResult.status === 'rejected'
										? settledResult.reason
										: new Error('Missing transaction signature');
								console.error('Failed to send close position batch:', error);
								failBatch(
									batchMarketIndexes,
									isTransactionSizeError(error) ? 'size_error' : String(error)
								);
							});

							// Queue settle pnl for auto-execution
							if (signedTxMap.settlePnl) {
								setVelocityStore((s) => {
									s.pnlsAwaitingAutoSettle = [
										...s.pnlsAwaitingAutoSettle,
										{
											marketIndexesOfPnls: marketIndexes,
											signedSettlePnlsTx: signedTxMap.settlePnl as
												| Transaction
												| VersionedTransaction,
										},
									];
								});
							}

							// Queue cancel orders for auto-execution
							if (signedTxMap.cancelOrders) {
								setVelocityStore((s) => {
									s.ordersAwaitingAutoCancel = [
										...s.ordersAwaitingAutoCancel,
										{
											marketIndexesOfOrders: marketIndexes,
											signedCancelExistingOrdersTx: signedTxMap.cancelOrders as
												| Transaction
												| VersionedTransaction,
										},
									];
								});
							}

							// The terminal toast (submitted / partial-failure warning / all-failed
							// error) is derived from this outcome by `success` below. Fills are
							// reported by the per-order market order toasts.
							return {
								successfulTxSigs,
								failedBatches,
								failedMarketNames,
							};
						} catch (e) {
							abortToasts([...pendingToastProps.keys()]);
							throw e;
						}
					},
					success: (r) => {
						if (r.failedBatches.length > 0) {
							const hasSizeError = r.failedBatches.includes('size_error');
							return {
								type: r.successfulTxSigs.length > 0 ? 'warning' : 'error',
								message: hasSizeError
									? 'Transaction too large. Please try closing fewer positions at once.'
									: `Failed to close ${r.failedMarketNames.join(', ')}.`,
								lengthMs: 5000,
							};
						}
						return {
							type: 'success',
							message: 'Orders submitted.',
							lengthMs: 4000,
						};
					},
					handleError: (error, id) => {
						// FeePayerError is already re-thrown by runOrderActionToast before
						// this runs.
						// Telemetry only - detection is owned by TransactionErrorHandler
						// (shared matcher), and messaging is handled by handleTxError below.
						if (isTransactionTooLargeError(error as AnchorError)) {
							captureEvent('trade_close_multiple_positions_size_error', {
								market_indexes: marketIndexes,
								error_message:
									error instanceof Error ? error.message : String(error),
							});
						}

						handleTxError(error, id);
					},
					errorLogLabel: 'Error closing multiple positions',
				});

				return result;
			},
			[
				appEventEmitter,
				currentUserAccount,
				velocityClient,
				getTxParams,
				handleTxError,
				setVelocityStore,
				slotDurationMs,
			]
		)
	);

	return closeMultiplePositions;
}
